/**
 * Hook that subscribes to service initialization progress and exposes
 * the state needed to render the Initialization Loading screen.
 */

import { useEffect, useState } from "react";

import { EventBus } from "../../../platform/events/EventBus";
import { loadServicesConfig } from "../../../platform/services/config/loadConfig";
import { ServiceInitializationProgress } from "../../../platform/services/ServiceRegistry";
import { useStateMachineContext } from "../../../platform/state/StateMachineProvider";
import { StateEvent } from "../../../platform/state/types";
import debugLogger from "../../../platform/utils/debugLogger";
import { InitializationError } from "../components/InitializationError";
import { isCriticalService } from "../components/loadingHelpers";
import { getMessageForProgress } from "../components/loadingMessages";

interface UseServiceInitializationOptions {
  eventBus?: EventBus;
  stateMachine: ReturnType<typeof useStateMachineContext> | null;
}

interface ServiceInitializationState {
  overallProgress: number;
  currentMessage: string;
  initializationErrors: InitializationError[];
  hasErrors: boolean;
  isGateBlocked: boolean;
  isInitializing: boolean;
  totalEnabledServices: number;
  completedServices: number;
  initializedServices: Set<string>;
}

export const useServiceInitialization = ({
  eventBus,
  stateMachine,
}: UseServiceInitializationOptions): ServiceInitializationState => {
  const [overallProgress, setOverallProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(
    "Preparing your workspace...",
  );
  const [initializationErrors, setInitializationErrors] = useState<
    InitializationError[]
  >([]);
  const [hasErrors, setHasErrors] = useState(false);
  const [isGateBlocked, setIsGateBlocked] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Progress calculation based on YAML service count
  const [totalEnabledServices, setTotalEnabledServices] = useState(0);
  const [completedServices, setCompletedServices] = useState(0);
  const [initializedServices, setInitializedServices] = useState<Set<string>>(
    new Set(),
  );

  // Get service registry from state machine (if available)
  const serviceRegistry = stateMachine?.getServiceRegistry();

  // Load service config to count total enabled services
  useEffect(() => {
    const loadServiceCount = async () => {
      try {
        const serviceConfigs = await loadServicesConfig();
        // Count only enabled services (enabled !== false means enabled)
        const enabledCount = serviceConfigs.filter(
          (service) => service.enabled !== false,
        ).length;
        setTotalEnabledServices(enabledCount);
        debugLogger.debug(`Total enabled services: ${enabledCount}`);
      } catch (error) {
        debugLogger.error(
          "Failed to load service config for progress calculation:",
          error,
        );
        // Fallback: use a default count if config can't be loaded
        setTotalEnabledServices(10);
      }
    };

    void loadServiceCount();
  }, []);

  // Subscribe to service initialization progress (behind the scenes)
  // Subscribe to EventBus directly - works even without state machine context
  useEffect(() => {
    if (!eventBus) return;

    // Track services internally
    let internalServices: Map<string, { status: string; error?: Error }> =
      new Map();

    const handleProgress = (progress: ServiceInitializationProgress) => {
      // Update internal tracking (not shown to user)
      internalServices.set(progress.serviceName, {
        status: progress.status,
        error: progress.error,
      });

      // Use progress value from ServiceRegistry (most accurate)
      // ServiceRegistry calculates progress based on actual initialization order
      if (progress.progress !== undefined) {
        const newProgress = Math.max(0, Math.min(100, progress.progress));
        setOverallProgress(newProgress);

        // Update message based on progress
        if (newProgress < 100) {
          const newMessage = getMessageForProgress(newProgress);
          setCurrentMessage(newMessage);
        }
      }

      // Track completed services for additional validation
      if (progress.status === "completed" && progress.serviceName !== "all") {
        setInitializedServices((prev) => {
          if (prev.has(progress.serviceName)) {
            return prev; // Already counted
          }
          const updated = new Set(prev);
          updated.add(progress.serviceName);

          // Update completed count
          const newCount = updated.size;
          setCompletedServices(newCount);

          return updated;
        });
      }

      // Handle errors (log to console, show user-friendly error if critical)
      if (progress.status === "failed" && progress.error) {
        const error: InitializationError = {
          stepId: progress.serviceName,
          stepName: progress.serviceName,
          error: progress.error.message,
          details: progress.error.stack,
          timestamp: progress.timestamp,
          severity: isCriticalService(progress.serviceName)
            ? "critical"
            : "error",
          retryable: !isCriticalService(progress.serviceName),
        };

        // Log technical details for debugging
        debugLogger.error(
          `Service initialization failed: ${progress.serviceName}`,
          {
            error: progress.error.message,
            stack: progress.error.stack,
            timestamp: progress.timestamp,
          },
        );

        setInitializationErrors((prev) => [...prev, error]);

        // Show error to user for ALL errors (not just critical)
        setHasErrors(true);
      }

      // Check if all services are done
      if (progress.serviceName === "all" && progress.status === "completed") {
        // Ensure progress is at 100%
        setOverallProgress(100);
        setCompletedServices(totalEnabledServices);
        setIsInitializing(false);

        // Update message to completion message
        setCurrentMessage("Welcome to Rusha!");

        // Check gate status (only if state machine is available)
        if (stateMachine) {
          const canTransition = stateMachine.canTransition(
            StateEvent.INITIALIZATION_COMPLETE,
          );
          if (!canTransition) {
            setIsGateBlocked(true);
            debugLogger.debug(
              "Initialization gate is closed. Staying in initialization phase for debugging.",
            );
          }
        }
      }
    };

    // Subscribe via eventBus directly - this works regardless of state machine context
    const _subscriptionId = eventBus.subscribeGlobal(
      "SERVICE_INITIALIZATION_PROGRESS",
      (payload: unknown) => {
        handleProgress(payload as ServiceInitializationProgress);
      },
    );

    // Also subscribe directly to service registry if it supports callbacks and is available
    let unsubscribe: (() => void) | undefined;
    if (serviceRegistry?.onProgress) {
      unsubscribe = serviceRegistry.onProgress(handleProgress);
    }

    return () => {
      // Cleanup subscriptions
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [eventBus, serviceRegistry, totalEnabledServices]);

  return {
    overallProgress,
    currentMessage,
    initializationErrors,
    hasErrors,
    isGateBlocked,
    isInitializing,
    totalEnabledServices,
    completedServices,
    initializedServices,
  };
};
